import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFragment } from "@apollo/client/react";
import {
  Box,
  Button,
  EmptyState,
  Icon,
  ScrollArea,
  Tabs,
  TabsPanel,
  Text,
} from "@salestrak/ui";
import {
  WorkflowStageFragmentDoc,
  type IssueRow,
} from "#app/lib/data";
import type { IssueViewStage, IssueViewTab } from "./issue-view-types";
import { useViewerIsShareOnly } from "#app/lib/hooks/use-permission";
import { IssueFormsTab } from "./issue-forms-tab";
import { IssueViewHeader } from "./issue-view-header";
import { IssueViewSummary } from "./issue-view-summary";
import { ActivityList } from "./activity/activity-list";
import { ActivityListSkeleton } from "./activity/activity-list-skeleton";
import { ActivityProvider, type AttachmentsFocus } from "./activity/activity-context";
import { NoteComposer } from "./activity/note-composer";
import {
  useActivityContextValue,
  type UseActivityContextValueResult,
} from "#app/hooks/issue-view/use-activity-context-value";
import { useIssueViewData } from "#app/hooks/issue-view/use-issue-view-data";
import { useIssueMediaItems } from "#app/hooks/issue-view/use-issue-media-items";
import { IssueMediaTab } from "./issue-media-tab";
import { IssueViewCapabilitiesProvider } from "./issue-view-capabilities";
import { useCreateIssueMode } from "#app/lib/create-issue-context";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useFeatureFlags, FeatureFlagKey } from "#app/hooks/use-feature-flags";
import type { Omittable } from "@salestrak/ui";
import { LightboxProjectProvider, type LightboxProjectMedia } from "#app/components/media/media-lightbox";
import { useIssueAttachments } from "#app/lib/hooks/use-issue-attachments";
import { type MediaRow, renamable, useIssueMediaRows } from "./issue-media-columns";
import { formPhotosOnFile, useFormsStore } from "#app/lib/forms";
import { useFormFillDialogActions } from "#app/lib/form-fill-dialog-context";
import { AnalyticsContext, IssueEvent, trackEvent } from "#app/lib/analytics";

// Tabs/TabsPanel own their own `min-h-0` now, so what is left here is the two
// decisions only this screen can make: the tab shell claims the body's free
// space, and the panel — not a ScrollArea inside it — is the scroller.
// `gap-0`: the panel meets the strip's hairline directly, so the layout
// editor's sunken field reaches it with no background sliver between.
const TABS_ROOT = "flex-1 gap-0";
const TAB_PANEL = "overflow-y-auto";
const TABLE_TAB_PANEL = "flex min-h-0 flex-1 flex-col";

type IssueBodyProps = Omittable<{
  /** When set, view mode. When null, create mode (relies on surrounding CreateIssueProvider). */
  issue: IssueRow | null;
  focusNoteId?: string | null;
  /** Land the activity feed on its first unread comment. */
  focusUnread?: boolean;
  /** Scroll to + highlight this custom field row on the summary tab. */
  focusCustomFieldId?: string | null;
  /** Focus the summary's title input — a fresh nonce per request so a
   *  repeat request on the already-open issue re-fires. */
  focusTitleNonce?: number;
  activeTab: IssueViewTab;
  setActiveTab: (tab: IssueViewTab) => void;
  contentMaxWidth: number | undefined;
  isMobile: boolean;
  /** Render composer docked below list (desktop view). Sheet passes composer via the drawer footer. */
  inlineComposer?: boolean;
  /** Shared activity context (sheet reuses same ref in footer composer). */
  activityContext?: UseActivityContextValueResult;
}>;

export function IssueBody(props: IssueBodyProps) {
  return props.issue ? <ViewBody {...props} issue={props.issue} /> : <CreateBody {...props} />;
}

// Thin wrapper that decides whether to compute its own activity context or
// use one provided by the parent (mobile sheet does the latter — and we MUST
// not call useActivityContextValue twice for the same issue, since each call
// spins up its own useQuery + fetchMore, doubling network requests).
function ViewBody(props: IssueBodyProps & { issue: IssueRow }) {
  if (props.activityContext) {
    return <ViewBodyInner {...props} activityContext={props.activityContext} />;
  }
  return <ViewBodyWithOwnContext {...props} />;
}

function ViewBodyWithOwnContext(props: IssueBodyProps & { issue: IssueRow }) {
  const ownContext = useActivityContextValue(
    props.issue.issueId,
    props.focusNoteId,
    true,
    props.focusUnread,
  );
  return <ViewBodyInner {...props} activityContext={ownContext} />;
}

function ViewBodyInner({
  issue,
  activeTab,
  setActiveTab,
  contentMaxWidth,
  inlineComposer,
  activityContext,
  focusCustomFieldId,
  focusTitleNonce,
}: IssueBodyProps & { issue: IssueRow; activityContext: UseActivityContextValueResult }) {
  const { issue: liveIssue, stage, assignees, values, attachments, fieldMedia, formCount, workflowId, viewerAccessLevel, viewerCanEdit, address, issueTypes, customFields } =
    useIssueViewData(issue.issueId);
  const resolvedIssue = (liveIssue as IssueRow | null) ?? issue;
  const { stageMap } = useWorkspaceData();
  const cachedStageResult = useFragment({
    fragment: WorkflowStageFragmentDoc,
    fragmentName: "WorkflowStage",
    from: { __typename: "Stage", stageId: resolvedIssue.stageId },
  });
  const cachedStage = cachedStageResult.complete
    ? (cachedStageResult.data as IssueViewStage)
    : null;
  // A move returns `stageId` without the nested `stage`, which then names
  // the old stage until a refetch.
  const resolvedStage =
    (stage?.stageId === resolvedIssue.stageId ? stage : null) ??
    cachedStage ??
    stageMap.get(resolvedIssue.stageId) ??
    null;
  const resolvedAssignees = assignees ?? resolvedIssue.assignees;
  const resolvedValues =
    values === undefined ? resolvedIssue.values : values;
  const resolvedWorkflowId =
    workflowId === undefined ? resolvedIssue.workflowId : workflowId;
  const resolvedAddress =
    address === undefined ? resolvedIssue.address : address;
  const resolvedViewerAccessLevel =
    viewerAccessLevel === undefined
      ? issue.viewerAccessLevel
      : viewerAccessLevel;
  const resolvedViewerCanEdit =
    viewerCanEdit === undefined ? issue.viewerCanEdit : viewerCanEdit;

  const { value, loading, hasActivities, notes } = activityContext;
  // The Forms tab waits for the flags so a flag-off workspace never flashes it; share-only roles never see it.
  const { ready, hasFlag } = useFeatureFlags();
  const shareOnly = useViewerIsShareOnly();
  const showFormsTab = ready && hasFlag(FeatureFlagKey.FORMS) && !shareOnly;
  const formsStore = useFormsStore(resolvedIssue.issueId, { skip: !showFormsTab || !formCount });
  const formPhotos = useMemo(
    () => formPhotosOnFile(formsStore.forms, formsStore.submissions),
    [formsStore.forms, formsStore.submissions],
  );
  const mediaItems = useIssueMediaItems(
    customFields,
    resolvedValues,
    notes,
    attachments,
    fieldMedia,
    formPhotos,
  );
  const { open: openFormFill } = useFormFillDialogActions();
  const formFromLink = useCallback(
    (row: MediaRow) => {
      if (row.item.source !== "form") return undefined;
      const { submission } = row.item.onFile;
      return () => openFormFill({ issueId: submission.issueId, kind: "submission", submissionId: submission.submissionId });
    },
    [openFormFill],
  );
  const { rows: mediaRows, lightboxMedia } = useIssueMediaRows({ items: mediaItems, fromLink: formFromLink });
  const { rename: renameMedia } = useIssueAttachments(resolvedIssue.issueId);
  const [attachmentsFocus, setAttachmentsFocus] = useState<AttachmentsFocus | null>(null);
  const activityValue = useMemo(
    () => ({
      ...value,
      openAttachments:
        mediaItems.length > 0
          ? (focus: AttachmentsFocus) => {
              setAttachmentsFocus(focus);
              setActiveTab("media");
            }
          : undefined,
    }),
    [mediaItems.length, setActiveTab, value],
  );
  const projectMedia = useMemo<LightboxProjectMedia>(() => {
    const renamableIds = new Set(mediaRows.filter((row) => renamable(row.item)).map((row) => row.photoId));
    return {
      items: new Map(lightboxMedia.map((media) => [media.id, media])),
      rename: (id, name) => void renameMedia(id, name),
      canRename: (id) => !!resolvedViewerCanEdit && renamableIds.has(id),
    };
  }, [lightboxMedia, mediaRows, renameMedia, resolvedViewerCanEdit]);
  const resolveTab = useCallback(
    (tab: IssueViewTab): IssueViewTab =>
      tab === "forms" && ready && !showFormsTab ? "summary" : tab,
    [ready, showFormsTab],
  );
  const currentTab = resolveTab(activeTab);

  // Media-tab quick links: jump to the field row on summary (reusing the
  // deep-link focus flow) or scroll to the note on activity. The local focus
  // clears when leaving summary so a later jump to the same field re-fires.
  const [mediaFieldFocus, setMediaFieldFocus] = useState<string | null>(null);
  const handleTabChange = useCallback(
    (requested: IssueViewTab) => {
      if (resolveTab(requested) !== "summary") setMediaFieldFocus(null);
      if (requested !== "media") setAttachmentsFocus(null);
      // Store the request, not its resolution: `currentTab` re-resolves every
      // render, so a tab that is unavailable now — flags still in flight, note
      // media not loaded — is honored once it opens instead of being lost to a
      // one-time coercion written into parent state.
      setActiveTab(requested);
    },
    [resolveTab, setActiveTab],
  );
  const handleNavigateToField = useCallback(
    (customFieldId: string) => {
      setMediaFieldFocus(customFieldId);
      handleTabChange("summary");
    },
    [handleTabChange],
  );
  const { scrollToNote } = value;
  const handleNavigateToNote = useCallback(
    (noteId: string, parentNoteId?: string | null) => {
      handleTabChange("activity");
      // scrollToNote retries across frames, so it survives the tab content
      // mounting after this call.
      void scrollToNote?.(noteId, parentNoteId);
    },
    [handleTabChange, scrollToNote],
  );

  return (
    <IssueViewCapabilitiesProvider
      level={resolvedViewerAccessLevel}
      canEdit={resolvedViewerCanEdit}
    >
    <LightboxProjectProvider value={projectMedia}>
    <Box direction="column" fill="flex" minHeight={0} position="relative">
      <Tabs
        className={TABS_ROOT}
        value={currentTab}
        onValueChange={(v) => {
          trackEvent({
            context: AnalyticsContext.Issue,
            event: IssueEvent.SwitchDetailTab,
            data: { tab: v as IssueViewTab, via: "strip" },
          });
          handleTabChange(v as IssueViewTab);
        }}
      >
        <IssueViewHeader
          activeTab={activeTab}
          issue={resolvedIssue}
          onSelectTab={(tab) => {
            trackEvent({
              context: AnalyticsContext.Issue,
              event: IssueEvent.SwitchDetailTab,
              data: { tab, via: "more_menu" },
            });
            handleTabChange(tab);
          }}
          showFormsTab={showFormsTab}
          stage={resolvedStage}
          contentMaxWidth={contentMaxWidth}
        />
        <TabsPanel className={TAB_PANEL} value="summary">
          <IssueViewSummary
            issue={resolvedIssue}
            stage={resolvedStage}
            workflowId={resolvedWorkflowId}
            address={resolvedAddress}
            assignees={resolvedAssignees}
            issueTypes={issueTypes}
            values={resolvedValues}
            customFields={customFields}
            contentMaxWidth={contentMaxWidth}
            focusCustomFieldId={mediaFieldFocus ?? focusCustomFieldId}
            focusTitleNonce={focusTitleNonce}
            onNavigateTab={handleTabChange}
            activity={value}
            activityLoading={loading}
            notes={notes}
            mediaItems={mediaItems}
          />
        </TabsPanel>
        {showFormsTab ? (
          <TabsPanel className={TABLE_TAB_PANEL} value="forms">
            <IssueFormsTab issue={resolvedIssue} />
          </TabsPanel>
        ) : null}
        <TabsPanel className={TAB_PANEL} value="activity">
          {loading && !hasActivities ? (
            // The feed's own silhouette, in the feed's column, so the entries
            // land on the bars. Delayed: a reopened issue answers from cache.
            <Box align="center" paddingY="md">
              <Box fill="width" maxWidth={contentMaxWidth} paddingX="sm">
                <ActivityListSkeleton delayed />
              </Box>
            </Box>
          ) : (
            <ActivityProvider value={activityValue}>
              {hasActivities ? (
                <Box direction="column" fill="flex" minHeight={0}>
                  <ActivityList issueId={resolvedIssue.issueId} contentMaxWidth={contentMaxWidth} />
                </Box>
              ) : (
                <Box flex={1} align="center" justify="center" padding="xl">
                  <EmptyState
                    size="sm"
                    media="circle"
                    icon="activity"
                    title="No activity yet"
                    description="Comments and changes on this Project appear here."
                  />
                </Box>
              )}
              {inlineComposer && (
                <Box align="center">
                  <Box fill="width" maxWidth={contentMaxWidth} paddingX="sm" paddingBottom="sm">
                    <NoteComposer issueId={resolvedIssue.issueId} />
                  </Box>
                </Box>
              )}
            </ActivityProvider>
          )}
        </TabsPanel>
          <TabsPanel className={TAB_PANEL} value="media">
            <Box direction="column" fill="flex" minHeight={0}>
              <IssueMediaTab
                items={mediaItems}
                issueId={resolvedIssue.issueId}
                issueName={resolvedIssue.name}
                canEdit={resolvedViewerCanEdit}
                onNavigateToField={handleNavigateToField}
                onNavigateToNote={handleNavigateToNote}
                notes={value}
                focus={attachmentsFocus}
                onClearFocus={() => setAttachmentsFocus(null)}
              />
            </Box>
          </TabsPanel>
      </Tabs>
    </Box>
    </LightboxProjectProvider>
    </IssueViewCapabilitiesProvider>
  );
}

function CreateBody({ contentMaxWidth, isMobile }: IssueBodyProps) {
  const { submit, submitAttemptCount, submitted, canSubmit, missingRequiredCount, draft } = useCreateIssueMode()!;
  const [barDismissed, setBarDismissed] = useState(false);

  useEffect(() => {
    if (submitAttemptCount > 0) setBarDismissed(false);
  }, [submitAttemptCount]);

  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (!hasMountedRef.current) { hasMountedRef.current = true; return; }
    setBarDismissed(true);
  }, [draft.name, draft.values]);

  const dismissBar = useCallback(() => setBarDismissed(true), []);
  const showMissingBar = submitted && !canSubmit && !barDismissed;

  return (
    <Box direction="column" fill="flex" minHeight={0}>
      <Box direction="column" fill="flex" minHeight={0} position="relative">
        <IssueViewHeader />
        {isMobile ? (
          <IssueViewSummary contentMaxWidth={contentMaxWidth} />
        ) : (
          <ScrollArea>
            <IssueViewSummary contentMaxWidth={contentMaxWidth} />
          </ScrollArea>
        )}
        {showMissingBar && (
          // Two elements: the floating row must not eat clicks on the fields
          // under it, while the bar inside it stays clickable.
          <Box
            pointerEvents="none"
            position="absolute"
            bottom={isMobile ? 8 : 16}
            left={0}
            right={0}
            zIndex={50}
            paddingX="md"
            align="center"
          >
            {/* The whole bar submits, and it contains a real dismiss button —
                so the bar cannot be a button element itself. */}
            <Box
              pointerEvents="auto"
              surface="popover"
              border
              borderColor="destructive"
              rounded="lg"
              paddingX="md"
              paddingY="sm"
              cursor="pointer"
              role="button"
              tabIndex={0}
              onClick={submit}
            >
              <Box direction="row" gap="sm" align="center">
                <Icon name="alert-circle" size="xs" color="destructive" />
                <Text size="sm" weight="semibold">
                  {missingRequiredCount} required field{missingRequiredCount !== 1 ? "s" : ""} missing
                </Text>
                <Button variant="ghost" size="icon-xs" onClick={(e) => { e.stopPropagation(); dismissBar(); }}>
                  <Icon name="x" size="xs" />
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
